import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Play, Pause, Plus, Trash2, ChevronLeft, ChevronRight, CalendarDays, Target } from "lucide-react";
import { api } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import NewEntryDialog from "../../components/app/NewEntryDialog";

const FALLBACK_TASKS = ["Advisory", "Bid management", "Contract review", "Meetings", "Cost engineering", "Scheduling", "Project management"];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function startOfWeek(d = new Date()) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0,0,0,0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function dayKey(d) { return d.toISOString().slice(0, 10); }
function fmtWeekLabel(monday, sunday) {
  const sameMonth = monday.getMonth() === sunday.getMonth();
  const sameYear = monday.getFullYear() === sunday.getFullYear();
  const opts = { month: "short", day: "numeric" };
  const left = monday.toLocaleDateString(undefined, opts);
  const right = sunday.toLocaleDateString(undefined, sameMonth && sameYear ? { day: "numeric" } : opts);
  return `${left} – ${right}${sameYear ? `, ${sunday.getFullYear()}` : ""}`;
}
function isSameWeek(a, b) { return dayKey(startOfWeek(a)) === dayKey(startOfWeek(b)); }

export default function TimeTracker() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectTasksMap, setProjectTasksMap] = useState({});
  const [form, setForm] = useState({ project_id: "", task: FALLBACK_TASKS[0], task_id: null, notes: "" });
  const [timer, setTimer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState(null);
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [, setTick] = useState(0);

  const days = useMemo(() => {
    const s = startOfWeek(weekAnchor);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [weekAnchor]);
  const weekFrom = dayKey(days[0]);
  const weekTo = dayKey(days[6]);
  const isThisWeek = isSameWeek(weekAnchor, new Date());
  const weekLabel = fmtWeekLabel(days[0], days[6]);

  const loadStatic = async () => {
    const [p, c] = await Promise.all([api.projects(), api.clients()]);
    setProjects(p); setClients(c);
    if (!form.project_id && p.length) setForm((f) => ({ ...f, project_id: p[0].id }));
    // Load per-project task links in parallel — used by the entry dialog & inline form
    const linksArr = await Promise.all(p.map((proj) => api.projectTasks(proj.id).catch(() => [])));
    const map = {};
    p.forEach((proj, i) => { map[proj.id] = linksArr[i]; });
    setProjectTasksMap(map);
  };

  const loadEntries = async () => {
    const e = await api.entries({ date_from: weekFrom, date_to: weekTo });
    setEntries(e);
  };

  useEffect(() => { loadStatic(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadEntries(); /* eslint-disable-next-line */ }, [weekFrom, weekTo]);

  // Compatibility shim — some handlers below still call load() after mutations.
  const load = async () => { await loadEntries(); };

  // Auto-open dialog when arriving with ?new=1 (from sidebar "+ New timer").
  // Depends on `params` so that clicking the sidebar while already on /app/time also fires.
  useEffect(() => {
    if (params.get("new") === "1") {
      setDialogDate(null);
      setDialogOpen(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => {
    if (!timer) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [timer]);

  const projName = (id) => projects.find((p) => p.id === id)?.name || "";
  const clientOf = (id) => {
    const p = projects.find((x) => x.id === id);
    return p ? clients.find((c) => c.id === p.client_id) : null;
  };

  const weekTotal = entries.reduce((a, e) => a + e.hours, 0);

  const startTimer = () => {
    if (timer || !form.project_id) return;
    setTimer({ startedAt: Date.now(), ...form });
    toast({ title: "Timer started", description: `${projName(form.project_id)} — ${form.task}` });
  };
  const stopTimer = async () => {
    if (!timer) return;
    const secs = Math.max(1, Math.round((Date.now() - timer.startedAt) / 1000));
    const hours = Math.max(0.02, Math.round((secs / 3600) * 100) / 100);
    await addEntry({ ...timer, hours });
    setTimer(null);
    toast({ title: "Timer stopped", description: `Logged ${hours.toFixed(2)}h.` });
  };

  const addEntry = async (e) => {
    const p = projects.find((x) => x.id === e.project_id);
    const created = await api.createEntry({
      project_id: e.project_id, task: e.task, task_id: e.task_id || null,
      notes: e.notes || "",
      date: e.date || todayISO(), hours: Number(e.hours), billable: !!p?.billable,
    });
    setEntries((prev) => [created, ...prev]);
    return created;
  };

  // Handlers for NewEntryDialog
  const handleDialogStartTimer = ({ project_id, task, task_id, notes }) => {
    if (timer) {
      toast({ title: "Timer already running", description: "Stop the current timer first." });
      return;
    }
    setForm((f) => ({ ...f, project_id, task, task_id, notes }));
    setTimer({ startedAt: Date.now(), project_id, task, task_id, notes });
    toast({ title: "Timer started", description: `${projName(project_id)} — ${task}` });
  };
  const handleDialogSave = async (fields) => {
    const p = projects.find((x) => x.id === fields.project_id);
    await addEntry(fields);
    toast({
      title: "Entry added",
      description: `${fields.hours.toFixed(2)}h to ${p?.name || "project"} on ${fields.date}.`,
    });
  };
  const openDialogForDay = (d) => {
    setDialogDate(dayKey(d));
    setDialogOpen(true);
  };

  const remove = async (id) => {
    await api.deleteEntry(id);
    setEntries(entries.filter((e) => e.id !== id));
  };

  const running = timer ? Math.floor((Date.now() - timer.startedAt) / 1000) : 0;
  const fmt = (s) => `${String(Math.floor(s/3600)).padStart(2,"0")}:${String(Math.floor((s%3600)/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-sm text-[#6a736f]">Timesheet</div>
          <h1 className="font-display text-4xl mt-1">Track your time</h1>
        </div>
        <div className="text-right flex items-center gap-4 flex-wrap justify-end">
          <div>
            <div className="text-xs text-[#6a736f]">{isThisWeek ? "This week" : "Week total"}</div>
            <div className="font-display text-3xl">{weekTotal.toFixed(2)}h</div>
          </div>
          <button
            data-testid="open-new-entry"
            onClick={() => { setDialogDate(null); setDialogOpen(true); }}
            className="btn-coral px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
          >
            <Plus size={14} /> New entry
          </button>
        </div>
      </div>

      {/* Week navigator */}
      <div className="mt-5 bg-white rounded-xl border border-cream p-3 flex items-center gap-2 flex-wrap" data-testid="week-navigator">
        <button
          onClick={() => setWeekAnchor(addDays(weekAnchor, -7))}
          data-testid="week-prev"
          title="Previous week"
          className="p-2 rounded-md border border-cream hover:bg-cream-2 text-forest"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          onClick={() => setWeekAnchor(new Date())}
          disabled={isThisWeek}
          data-testid="week-today"
          className={`text-xs px-3 py-1.5 rounded-md border ${isThisWeek ? "border-cream text-[#6a736f] cursor-default" : "border-cream text-forest hover:bg-cream-2"}`}
        >
          Today
        </button>
        <button
          onClick={() => setWeekAnchor(addDays(weekAnchor, 7))}
          data-testid="week-next"
          title="Next week"
          className="p-2 rounded-md border border-cream hover:bg-cream-2 text-forest"
        >
          <ChevronRight size={16} />
        </button>
        <div className="flex items-center gap-2 pl-2 border-l border-cream">
          <CalendarDays size={14} className="text-[#6a736f]" />
          <span className="text-sm font-medium" data-testid="week-label">{weekLabel}</span>
        </div>
        <label className="ml-auto flex items-center gap-2 text-xs text-[#6a736f]">
          Jump to:
          <input
            type="date"
            value={dayKey(weekAnchor)}
            onChange={(e) => e.target.value && setWeekAnchor(new Date(`${e.target.value}T00:00:00`))}
            data-testid="week-jump"
            className="border border-cream rounded-md px-2 py-1 text-xs"
          />
        </label>
        <BudgetChip
          total={weekTotal}
          target={Number(user?.weekly_target_hours ?? 40)}
          weekStart={days[0]}
          weekEnd={days[6]}
        />
      </div>

      {projects.length === 0 ? (
        <div className="mt-6 bg-white rounded-xl border border-cream p-6 text-sm text-[#3f4a44]">
          You have no projects yet. Create one on the <a href="/app/projects" className="text-coral font-medium">Projects</a> page to start tracking time.
        </div>
      ) : (
        <>
          <div className="mt-6 bg-white rounded-xl border border-cream p-5 flex flex-col md:flex-row items-stretch md:items-center gap-3">
            <select value={form.project_id} onChange={(e) => {
              const pid = e.target.value;
              const list = projectTasksMap[pid] || [];
              const first = list[0];
              setForm({ ...form, project_id: pid,
                task_id: first ? first.task_id : null,
                task: first ? first.task_name : FALLBACK_TASKS[0] });
            }}
              className="border border-cream rounded-md px-3 py-2 text-sm min-w-[180px]">
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {(() => {
              const list = projectTasksMap[form.project_id] || [];
              if (list.length > 0) {
                return (
                  <select
                    value={form.task_id || ""}
                    onChange={(e) => {
                      const link = list.find((l) => l.task_id === e.target.value);
                      setForm({ ...form, task_id: e.target.value, task: link?.task_name || "" });
                    }}
                    className="border border-cream rounded-md px-3 py-2 text-sm min-w-[160px]">
                    {list.map((l) => (
                      <option key={l.task_id} value={l.task_id}>
                        {l.task_name}{l.effective_billable ? "" : " · non-billable"}
                      </option>
                    ))}
                  </select>
                );
              }
              return (
                <select value={form.task} onChange={(e) => setForm({ ...form, task: e.target.value, task_id: null })}
                  className="border border-cream rounded-md px-3 py-2 text-sm min-w-[140px]">
                  {FALLBACK_TASKS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              );
            })()}
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="What are you working on?"
              className="flex-1 border border-cream rounded-md px-3 py-2 text-sm" />
            <div className="font-display text-2xl tabular-nums text-forest min-w-[110px] text-center">{fmt(running)}</div>
            {timer ? (
              <button onClick={stopTimer} className="btn-outline-forest px-5 py-2 rounded-md text-sm font-medium flex items-center gap-2">
                <Pause size={16} /> Stop
              </button>
            ) : (
              <button onClick={startTimer} className="btn-coral px-5 py-2 rounded-md text-sm font-medium flex items-center gap-2">
                <Play size={16} /> Start
              </button>
            )}
          </div>

          <div className="mt-4 bg-cream-2/60 border border-cream rounded-xl p-4 flex flex-col md:flex-row gap-3 items-center">
            <div className="text-sm font-medium text-forest">Need to log a past entry?</div>
            <button
              type="button"
              data-testid="open-manual-entry"
              onClick={() => { setDialogDate(null); setDialogOpen(true); }}
              className="btn-outline-forest px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
            >
              <Plus size={16} /> Log entry
            </button>
            <div className="text-xs text-forest/60">
              Opens the new-entry popup with date, times and notes.
            </div>
          </div>

          <div className="mt-8 bg-white rounded-xl border border-cream overflow-hidden">
            <div className="grid grid-cols-8 text-xs text-[#6a736f] bg-cream-2/50">
              <div className="px-4 py-3">Project</div>
              {days.map((d) => (
                <button
                  key={dayKey(d)}
                  onClick={() => openDialogForDay(d)}
                  data-testid={`day-header-${dayKey(d)}`}
                  className="px-2 py-3 text-center hover:bg-cream-2 focus:outline-none focus:bg-cream-2 group"
                  title="Click to add a new entry for this day"
                >
                  <div>{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                  <div className="text-forest font-medium">{d.getDate()}</div>
                  <div className="text-[10px] text-coral opacity-0 group-hover:opacity-100">+ new</div>
                </button>
              ))}
            </div>
            {entries.map((e) => {
              const c = clientOf(e.project_id);
              return (
                <div key={e.id} className="grid grid-cols-8 items-center border-t border-cream text-sm hover:bg-cream-2/40">
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: c?.color }} />
                      <span className="font-medium">{projName(e.project_id)}</span>
                    </div>
                    <div className="text-xs text-[#6a736f] pl-4">{e.task} · {e.notes || "—"}</div>
                  </div>
                  {days.map((d) => (
                    <div key={dayKey(d)} className="px-2 py-3 text-center tabular-nums text-forest">
                      {dayKey(d) === e.date ? e.hours.toFixed(2) : ""}
                    </div>
                  ))}
                </div>
              );
            })}
            <div className="grid grid-cols-8 border-t border-cream bg-cream-2/40 text-sm">
              <div className="px-4 py-3 font-semibold">Daily total</div>
              {days.map((d) => {
                const total = entries.filter((e) => e.date === dayKey(d)).reduce((a, e) => a + e.hours, 0);
                return <div key={dayKey(d)} className="px-2 py-3 text-center tabular-nums font-semibold">{total ? total.toFixed(2) : "—"}</div>;
              })}
            </div>
          </div>

          <div className="mt-8">
            <div className="font-semibold mb-3">Recent entries</div>
            <div className="bg-white rounded-xl border border-cream divide-y divide-[#EAF3EE]">
              {entries.slice(0, 10).map((e) => (
                <div key={e.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="text-sm">
                    <div className="font-medium">{projName(e.project_id)} — <span className="text-[#6a736f]">{e.task}</span></div>
                    <div className="text-xs text-[#6a736f]">{e.date} · {e.notes || "—"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm tabular-nums">{e.hours.toFixed(2)}h</div>
                    <button onClick={() => remove(e.id)} className="text-[#6a736f] hover:text-coral"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
              {entries.length === 0 && <div className="px-4 py-6 text-sm text-[#6a736f]">No entries yet this week.</div>}
            </div>
          </div>
        </>
      )}

      <NewEntryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        projects={projects}
        clients={clients}
        projectTasks={projectTasksMap}
        fallbackTasks={FALLBACK_TASKS}
        initialDate={dialogDate}
        canStartTimer={!timer}
        onStartTimer={handleDialogStartTimer}
        onSaveEntry={handleDialogSave}
      />
    </div>
  );
}


// -------------------------------------------------------------------------
// Weekly budget chip — shows tracked hours vs the user's target, with a
// pro-rated colour so today's expected progress lights up amber/green/red.
// -------------------------------------------------------------------------
function BudgetChip({ total, target, weekStart, weekEnd }) {
  if (!target || target <= 0) return null;
  const now = new Date();
  const startMs = weekStart.getTime();
  const endMs = weekEnd.getTime() + 24 * 3600 * 1000; // include Sunday

  // Determine expected hours for THIS point in time.
  //   • past week   → expected = target (should have completed)
  //   • future week → expected = 0     (pre-emptive entries, no pressure)
  //   • current wk  → expected = target × (business days elapsed / 5)
  let expected = target;
  let periodLabel = "target";
  if (now.getTime() < startMs) {
    expected = 0;
    periodLabel = "future week";
  } else if (now.getTime() > endMs) {
    expected = target;
    periodLabel = "target";
  } else {
    // Business days elapsed (Mon–Fri only) up to and including today
    let elapsed = 0;
    const cursor = new Date(weekStart);
    while (cursor.getTime() <= Math.min(now.getTime(), endMs)) {
      const d = cursor.getDay();
      if (d >= 1 && d <= 5) elapsed++;
      cursor.setDate(cursor.getDate() + 1);
    }
    expected = target * (Math.min(elapsed, 5) / 5);
    periodLabel = "so far";
  }

  const pct = target > 0 ? Math.min(999, (total / target) * 100) : 0;
  let tone = "neutral";
  if (expected === 0) tone = "neutral";
  else if (total >= target) tone = "good";
  else if (total >= expected * 0.95) tone = "good";
  else if (total >= expected * 0.7) tone = "warn";
  else tone = "bad";

  const styles = {
    good:    { bg: "bg-emerald-100", text: "text-emerald-800", bar: "bg-emerald-600" },
    warn:    { bg: "bg-amber-100",   text: "text-amber-800",   bar: "bg-amber-500" },
    bad:     { bg: "bg-coral/15",    text: "text-coral",       bar: "bg-coral" },
    neutral: { bg: "bg-cream-2",     text: "text-[#3f4a44]",   bar: "bg-forest/60" },
  }[tone];

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium ${styles.bg} ${styles.text}`}
      data-testid="budget-chip"
      title={
        tone === "good"   ? `On track — ${expected.toFixed(1)}h expected ${periodLabel}` :
        tone === "warn"   ? `Slightly behind — ${expected.toFixed(1)}h expected ${periodLabel}` :
        tone === "bad"    ? `Under target — ${expected.toFixed(1)}h expected ${periodLabel}` :
        `Weekly target ${target}h`
      }
    >
      <Target size={12} />
      <span className="tabular-nums" data-testid="budget-chip-text">
        {total.toFixed(2)}h / {target}h
      </span>
      <div className="w-16 h-1.5 rounded-full bg-white/60 overflow-hidden">
        <div className={`h-full ${styles.bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}
